<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import TextArea from 'next/textarea/TextArea.vue';
import InboxesAPI from 'dashboard/api/inboxes';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
  active: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const flows = ref([]);
const isLoading = ref(false);
const isSubmitting = ref(false);
const publishingFlowId = ref('');
const previewingFlowId = ref('');
const editingFlowId = ref('');
const deletingFlowId = ref('');
const deprecatingFlowId = ref('');
const loadingAssetsFlowId = ref('');
const loadingMetricsFlowId = ref('');
const assetsByFlowId = ref({});
const metricsByFlowId = ref({});
const previewByFlowId = ref({});
const previewPayloadByFlowId = ref({});
const previewErrorByFlowId = ref({});
const encryptionPayload = ref({});
const encryptionKey = ref('');
const isSavingEncryption = ref(false);
const showCreateForm = ref(false);
const hasLoaded = ref(false);
const formContainerRef = ref(null);
const searchQuery = ref('');
const statusFilter = ref('all');
const editForm = ref({
  name: '',
  endpoint_uri: '',
  categories: [],
});
const form = ref({
  name: '',
  endpoint_uri: '',
  categories: ['OTHER'],
});

const categoryOptions = computed(() => [
  { value: 'OTHER', label: t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_CATEGORY_OTHER') },
  { value: 'SIGN_UP', label: t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_CATEGORY_SIGN_UP') },
  { value: 'SIGN_IN', label: t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_CATEGORY_SIGN_IN') },
  {
    value: 'APPOINTMENT_BOOKING',
    label: t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_CATEGORY_APPOINTMENT_BOOKING'),
  },
  {
    value: 'LEAD_GENERATION',
    label: t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_CATEGORY_LEAD_GENERATION'),
  },
  { value: 'CONTACT_US', label: t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_CATEGORY_CONTACT_US') },
  {
    value: 'CUSTOMER_SUPPORT',
    label: t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_CATEGORY_CUSTOMER_SUPPORT'),
  },
  { value: 'SURVEY', label: t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_CATEGORY_SURVEY') },
]);

const hasFlows = computed(() => flows.value.length > 0);
const filteredFlows = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();

  return flows.value.filter(flow => {
    const status = flow.status?.toString().toLowerCase() || 'draft';
    const categories = formatCategories(flow).toLowerCase();
    const matchesQuery =
      !query ||
      (flow.name || flow.id || '').toString().toLowerCase().includes(query) ||
      (flow.id || '').toString().toLowerCase().includes(query) ||
      categories.includes(query);

    if (!matchesQuery) return false;
    if (statusFilter.value === 'all') return true;

    return status.includes(statusFilter.value);
  });
});
const canCreateFlow = computed(
  () => form.value.name.trim() && form.value.categories.length > 0
);
const flowStats = computed(() => {
  const summary = {
    total: flows.value.length,
    published: 0,
    draft: 0,
    deprecated: 0,
  };

  flows.value.forEach(flow => {
    const status = flow.status?.toString().toUpperCase() || 'DRAFT';
    if (status.includes('PUBLISH')) summary.published += 1;
    else if (status.includes('DEPREC')) summary.deprecated += 1;
    else summary.draft += 1;
  });

  return summary;
});
const previewReadyCount = computed(
  () => Object.values(previewByFlowId.value).filter(Boolean).length
);
const metricsLoadedCount = computed(
  () => Object.keys(metricsByFlowId.value).length
);
const assetsLoadedCount = computed(
  () => Object.keys(assetsByFlowId.value).length
);

const resetForm = () => {
  form.value = {
    name: '',
    endpoint_uri: '',
    categories: ['OTHER'],
  };
};

const loadFlows = async () => {
  isLoading.value = true;
  try {
    const response = await InboxesAPI.getWhatsAppFlows(props.inbox.id);
    flows.value = response.data?.payload || [];
    await loadEncryption();
    hasLoaded.value = true;
  } catch (error) {
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const toggleCreateForm = () => {
  showCreateForm.value = !showCreateForm.value;
  if (!showCreateForm.value) {
    resetForm();
    return;
  }

  nextTick(() => {
    formContainerRef.value?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  });
};

const startEditing = flow => {
  editingFlowId.value = flow.id;
  editForm.value = {
    name: flow.name || '',
    endpoint_uri: flow.endpoint_uri || '',
    categories: Array(flow.categories || []),
  };
};

const cancelEditing = () => {
  editingFlowId.value = '';
};

const toggleCategory = (category, target = 'create') => {
  const targetForm = target === 'edit' ? editForm.value : form.value;

  if (targetForm.categories.includes(category)) {
    targetForm.categories = targetForm.categories.filter(item => item !== category);
    if (!targetForm.categories.length) {
      targetForm.categories = ['OTHER'];
    }
    return;
  }

  targetForm.categories = [...targetForm.categories, category];
};

const createFlow = async () => {
  isSubmitting.value = true;
  try {
    const response = await InboxesAPI.createWhatsAppFlow(props.inbox.id, form.value);
    flows.value = response.data?.payload || flows.value;
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_CREATE_SUCCESS'));
    resetForm();
    showCreateForm.value = false;
  } catch (error) {
    const message =
      error?.response?.data?.error ||
      t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_CREATE_ERROR');
    useAlert(message);
  } finally {
    isSubmitting.value = false;
  }
};

const updateFlow = async flow => {
  try {
    const response = await InboxesAPI.updateWhatsAppFlow(
      props.inbox.id,
      flow.id,
      editForm.value
    );
    flows.value = response.data?.payload || flows.value;
    editingFlowId.value = '';
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_UPDATE_SUCCESS'));
  } catch (error) {
    const message =
      error?.response?.data?.error ||
      t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_UPDATE_ERROR');
    useAlert(message);
  }
};

const publishFlow = async flow => {
  publishingFlowId.value = flow.id;
  try {
    const response = await InboxesAPI.publishWhatsAppFlow(props.inbox.id, flow.id);
    flows.value = response.data?.payload || flows.value;
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_PUBLISH_SUCCESS'));
  } catch (error) {
    const message =
      error?.response?.data?.error ||
      t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_PUBLISH_ERROR');
    useAlert(message);
  } finally {
    publishingFlowId.value = '';
  }
};

const openPreview = async flow => {
  previewingFlowId.value = flow.id;
  previewErrorByFlowId.value = {
    ...previewErrorByFlowId.value,
    [flow.id]: '',
  };
  try {
    const response = await InboxesAPI.previewWhatsAppFlow(props.inbox.id, flow.id);
    previewPayloadByFlowId.value = {
      ...previewPayloadByFlowId.value,
      [flow.id]: response.data,
    };
    const previewUrl =
      response.data?.preview_url ||
      response.data?.preview?.preview_url ||
      response.data?.preview?.url;

    if (!previewUrl) {
      const message =
        response.data?.error ||
        t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_PREVIEW_ERROR');
      previewErrorByFlowId.value = {
        ...previewErrorByFlowId.value,
        [flow.id]: message,
      };
      useAlert(message);
      return;
    }

    previewByFlowId.value = {
      ...previewByFlowId.value,
      [flow.id]: previewUrl,
    };
    window.open(previewUrl, '_blank', 'noopener');
  } catch (error) {
    const message =
      error?.response?.data?.error ||
      t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_PREVIEW_ERROR');
    previewPayloadByFlowId.value = {
      ...previewPayloadByFlowId.value,
      [flow.id]: error?.response?.data || {},
    };
    previewErrorByFlowId.value = {
      ...previewErrorByFlowId.value,
      [flow.id]: message,
    };
    useAlert(message);
  } finally {
    previewingFlowId.value = '';
  }
};

const deprecateFlow = async flow => {
  deprecatingFlowId.value = flow.id;
  try {
    const response = await InboxesAPI.deprecateWhatsAppFlow(
      props.inbox.id,
      flow.id
    );
    flows.value = response.data?.payload || flows.value;
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_DEPRECATE_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_DEPRECATE_ERROR')
    );
  } finally {
    deprecatingFlowId.value = '';
  }
};

const deleteFlow = async flow => {
  deletingFlowId.value = flow.id;
  try {
    const response = await InboxesAPI.deleteWhatsAppFlow(props.inbox.id, flow.id);
    flows.value = response.data?.payload || [];
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_DELETE_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_DELETE_ERROR')
    );
  } finally {
    deletingFlowId.value = '';
  }
};

const loadAssets = async flow => {
  loadingAssetsFlowId.value = flow.id;
  try {
    const response = await InboxesAPI.getWhatsAppFlowAssets(
      props.inbox.id,
      flow.id
    );
    assetsByFlowId.value = {
      ...assetsByFlowId.value,
      [flow.id]: response.data?.payload || [],
    };
  } catch (error) {
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_ASSETS_ERROR'));
  } finally {
    loadingAssetsFlowId.value = '';
  }
};

const loadMetrics = async flow => {
  loadingMetricsFlowId.value = flow.id;
  try {
    const response = await InboxesAPI.getWhatsAppFlowMetrics(
      props.inbox.id,
      flow.id,
      {
        start: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000)
          .toISOString()
          .slice(0, 10),
        end: new Date().toISOString().slice(0, 10),
        granularity: 'DAILY',
      }
    );
    metricsByFlowId.value = {
      ...metricsByFlowId.value,
      [flow.id]: response.data?.payload || {},
    };
  } catch (error) {
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_METRICS_ERROR'));
  } finally {
    loadingMetricsFlowId.value = '';
  }
};

const loadEncryption = async () => {
  try {
    const response = await InboxesAPI.getWhatsAppFlowEncryption(props.inbox.id);
    encryptionPayload.value = response.data?.payload || {};
    encryptionKey.value =
      response.data?.payload?.business_public_key ||
      response.data?.payload?.public_key ||
      '';
  } catch (error) {
    // silent load failure for optional section
  }
};

const saveEncryption = async () => {
  isSavingEncryption.value = true;
  try {
    const response = await InboxesAPI.updateWhatsAppFlowEncryption(
      props.inbox.id,
      encryptionKey.value
    );
    encryptionPayload.value = response.data?.payload || {};
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_ENCRYPTION_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_ENCRYPTION_ERROR')
    );
  } finally {
    isSavingEncryption.value = false;
  }
};

const formatCategories = flow => {
  return Array(flow.categories || []).join(', ') || 'OTHER';
};

const collectMetricSummary = value => {
  const metrics = [];

  const visit = (node, path = []) => {
    if (typeof node === 'number' && Number.isFinite(node)) {
      metrics.push({
        key: path.join('.'),
        label: path[path.length - 1] || 'value',
        value: node,
      });
      return;
    }

    if (Array.isArray(node)) {
      node.forEach(item => visit(item, path));
      return;
    }

    if (node && typeof node === 'object') {
      Object.entries(node).forEach(([key, nestedValue]) => {
        if (['start', 'end', 'timestamp'].includes(key)) return;
        visit(nestedValue, [...path, key]);
      });
    }
  };

  visit(value);

  return metrics
    .sort((left, right) => Math.abs(right.value) - Math.abs(left.value))
    .slice(0, 6);
};

const copyFlowId = async flow => {
  try {
    await navigator.clipboard.writeText(flow.id);
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_COPY_ID_SUCCESS'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_COPY_ID_ERROR'));
  }
};

const copyPreviewUrl = async flowId => {
  const previewUrl = previewByFlowId.value[flowId];
  if (!previewUrl) return;

  try {
    await navigator.clipboard.writeText(previewUrl);
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_COPY_PREVIEW_SUCCESS'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_COPY_PREVIEW_ERROR'));
  }
};

watch(
  () => props.active,
  isActive => {
    if (isActive && !hasLoaded.value) {
      loadFlows();
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="grid gap-4">
    <div
      v-if="hasFlows"
      class="grid gap-3 sm:grid-cols-2 xl:grid-cols-4"
    >
      <div class="rounded-xl border border-n-container bg-n-alpha-2 p-4">
        <p class="mb-1 text-xs uppercase tracking-wide text-n-slate-10">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_STAT_TOTAL') }}
        </p>
        <p class="mb-0 text-2xl font-semibold text-n-slate-12">
          {{ flowStats.total }}
        </p>
      </div>
      <div class="rounded-xl border border-n-container bg-n-alpha-2 p-4">
        <p class="mb-1 text-xs uppercase tracking-wide text-n-slate-10">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_STAT_PUBLISHED') }}
        </p>
        <p class="mb-0 text-2xl font-semibold text-n-slate-12">
          {{ flowStats.published }}
        </p>
      </div>
      <div class="rounded-xl border border-n-container bg-n-alpha-2 p-4">
        <p class="mb-1 text-xs uppercase tracking-wide text-n-slate-10">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_STAT_DRAFT') }}
        </p>
        <p class="mb-0 text-2xl font-semibold text-n-slate-12">
          {{ flowStats.draft }}
        </p>
      </div>
      <div class="rounded-xl border border-n-container bg-n-alpha-2 p-4">
        <p class="mb-1 text-xs uppercase tracking-wide text-n-slate-10">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_STAT_DEPRECATED') }}
        </p>
        <p class="mb-0 text-2xl font-semibold text-n-slate-12">
          {{ flowStats.deprecated }}
        </p>
      </div>
      <div class="rounded-xl border border-n-container bg-n-alpha-2 p-4">
        <p class="mb-1 text-xs uppercase tracking-wide text-n-slate-10">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_STAT_PREVIEW_READY') }}
        </p>
        <p class="mb-0 text-2xl font-semibold text-n-slate-12">
          {{ previewReadyCount }}
        </p>
      </div>
      <div class="rounded-xl border border-n-container bg-n-alpha-2 p-4">
        <p class="mb-1 text-xs uppercase tracking-wide text-n-slate-10">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_STAT_METRICS_READY') }}
        </p>
        <p class="mb-0 text-2xl font-semibold text-n-slate-12">
          {{ metricsLoadedCount }}
        </p>
      </div>
      <div class="rounded-xl border border-n-container bg-n-alpha-2 p-4">
        <p class="mb-1 text-xs uppercase tracking-wide text-n-slate-10">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_STAT_ASSETS_READY') }}
        </p>
        <p class="mb-0 text-2xl font-semibold text-n-slate-12">
          {{ assetsLoadedCount }}
        </p>
      </div>
    </div>

    <div class="flex flex-wrap items-center gap-3">
      <Button
        type="button"
        color="blue"
        icon="i-ph-arrows-clockwise"
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_REFRESH')"
        :is-loading="isLoading"
        @click="loadFlows"
      />
      <button
        type="button"
        class="inline-flex items-center gap-2 rounded-lg border border-n-container bg-n-background px-4 py-2 text-sm font-medium text-n-slate-12 transition-colors hover:bg-n-alpha-2"
        :aria-expanded="showCreateForm"
        @click.prevent="toggleCreateForm"
      >
        <span class="i-ph-plus text-base" aria-hidden="true" />
        {{
          showCreateForm
            ? $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_CANCEL_CREATE')
            : $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_NEW')
        }}
      </button>
    </div>

    <div class="grid gap-4 rounded-xl border border-n-container bg-n-alpha-2 p-4 lg:grid-cols-[minmax(0,2fr)_minmax(12rem,1fr)]">
      <woot-input
        v-model="searchQuery"
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_SEARCH')"
        :placeholder="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_SEARCH_PLACEHOLDER')"
      />
      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        <span class="font-medium">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_STATUS_FILTER') }}
        </span>
        <select
          v-model="statusFilter"
          class="w-full rounded-lg border border-n-container bg-n-background px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
        >
          <option value="all">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_STATUS_ALL') }}
          </option>
          <option value="publish">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_STATUS_PUBLISHED') }}
          </option>
          <option value="draft">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_STATUS_DRAFT') }}
          </option>
          <option value="deprec">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_STATUS_DEPRECATED') }}
          </option>
        </select>
      </label>
    </div>

    <p class="mb-0 text-xs text-n-slate-11">
      {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_NOTE') }}
    </p>

    <div class="rounded-xl border border-n-container bg-n-alpha-2 p-4">
      <TextArea
        v-model="encryptionKey"
        :label="
          $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_ENCRYPTION_PUBLIC_KEY')
        "
        auto-height
        min-height="5rem"
        max-height="12rem"
      />
      <div class="mt-4 flex justify-end">
        <Button
          type="button"
          color="blue"
          icon="i-ph-key"
          :label="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_ENCRYPTION_SAVE')
          "
          :is-loading="isSavingEncryption"
          @click="saveEncryption"
        />
      </div>
    </div>

    <div
      v-if="showCreateForm"
      ref="formContainerRef"
      class="rounded-xl border border-n-container bg-n-alpha-2 p-4"
    >
      <div class="mb-4 rounded-xl border border-n-brand/30 bg-n-brand/5 px-4 py-3">
        <p class="mb-1 text-sm font-semibold text-n-slate-12">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_NEW') }}
        </p>
        <p class="mb-0 text-xs text-n-slate-11">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_NOTE') }}
        </p>
      </div>

      <div class="grid gap-4 lg:grid-cols-2">
        <woot-input
          v-model="form.name"
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_NAME')"
          :placeholder="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_NAME_PLACEHOLDER')
          "
        />
        <woot-input
          v-model="form.endpoint_uri"
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_ENDPOINT')"
          :placeholder="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_ENDPOINT_PLACEHOLDER')
          "
        />
      </div>

      <div class="mt-4 grid gap-2">
        <p class="mb-0 text-sm font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_CATEGORIES') }}
        </p>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="option in categoryOptions"
            :key="option.value"
            type="button"
            class="rounded-full border px-3 py-1.5 text-xs font-medium transition-colors"
            :class="
              form.categories.includes(option.value)
                ? 'border-n-brand bg-n-brand/10 text-n-brand'
                : 'border-n-container bg-n-background text-n-slate-11 hover:border-n-brand'
            "
            @click="toggleCategory(option.value)"
          >
            {{ option.label }}
          </button>
        </div>
      </div>

      <div class="mt-4 flex justify-end">
        <Button
          type="button"
          color="blue"
          icon="i-ph-rocket-launch"
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_CREATE_BUTTON')"
          :disabled="!canCreateFlow"
          :is-loading="isSubmitting"
          @click="createFlow"
        />
      </div>
    </div>

    <div
      v-if="isLoading"
      class="rounded-xl border border-n-container bg-n-alpha-2 px-4 py-10 text-center text-sm text-n-slate-11"
    >
      {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_LOADING') }}
    </div>

    <div
      v-else-if="!hasFlows"
      class="rounded-xl border border-dashed border-n-container bg-n-alpha-2 px-4 py-10 text-center text-sm text-n-slate-11"
    >
      {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_EMPTY') }}
    </div>

    <div v-else-if="!filteredFlows.length" class="rounded-xl border border-dashed border-n-container bg-n-alpha-2 px-4 py-10 text-center text-sm text-n-slate-11">
      {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_FILTER_EMPTY') }}
    </div>

    <div v-else class="grid gap-3">
      <div
        v-for="flow in filteredFlows"
        :key="flow.id"
        class="rounded-xl border border-n-container bg-n-alpha-2 p-4"
      >
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <div class="flex flex-wrap items-center gap-2">
              <p class="mb-0 truncate text-sm font-semibold text-n-slate-12">
                {{ flow.name || flow.id }}
              </p>
              <span class="rounded-full bg-n-alpha-3 px-2 py-0.5 text-[11px] font-medium text-n-slate-11">
                {{ flow.status || 'DRAFT' }}
              </span>
              <button
                type="button"
                class="rounded-full bg-n-alpha-3 px-2 py-0.5 text-[11px] font-medium text-n-slate-11 hover:bg-n-alpha-4"
                @click="copyFlowId(flow)"
              >
                {{ flow.id }}
              </button>
            </div>
            <p class="mt-1 mb-0 text-xs text-n-slate-11">
              {{ formatCategories(flow) }}
            </p>
          </div>

          <div class="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="faded"
              color="slate"
              icon="i-ph-pencil-simple"
              :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_EDIT')"
              @click="startEditing(flow)"
            />
            <Button
              type="button"
              variant="faded"
              color="slate"
              icon="i-ph-eye"
              :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_PREVIEW')"
              :is-loading="previewingFlowId === flow.id"
              :disabled="previewingFlowId === flow.id"
              @click="openPreview(flow)"
            />
            <Button
              type="button"
              color="blue"
              icon="i-ph-paper-plane-tilt"
              :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_PUBLISH')"
              :is-loading="publishingFlowId === flow.id"
              :disabled="publishingFlowId === flow.id"
              @click="publishFlow(flow)"
            />
            <Button
              type="button"
              variant="faded"
              color="slate"
              icon="i-ph-chart-line"
              :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_METRICS')"
              :is-loading="loadingMetricsFlowId === flow.id"
              @click="loadMetrics(flow)"
            />
            <Button
              type="button"
              variant="faded"
              color="slate"
              icon="i-ph-file-code"
              :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_ASSETS')"
              :is-loading="loadingAssetsFlowId === flow.id"
              @click="loadAssets(flow)"
            />
            <Button
              type="button"
              variant="faded"
              color="slate"
              icon="i-ph-archive-box"
              :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_DEPRECATE')"
              :is-loading="deprecatingFlowId === flow.id"
              @click="deprecateFlow(flow)"
            />
            <Button
              type="button"
              variant="faded"
              color="slate"
              icon="i-lucide-trash-2"
              :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_DELETE')"
              :is-loading="deletingFlowId === flow.id"
              @click="deleteFlow(flow)"
            />
          </div>
        </div>

        <div class="mt-3 text-xs text-n-slate-11">
          <span class="font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_ENDPOINT') }}:
          </span>
          {{ flow.endpoint_uri || '—' }}
        </div>

        <div
          v-if="previewErrorByFlowId[flow.id]"
          class="mt-3 rounded-xl border border-n-ruby-6 bg-n-ruby-3/20 p-3 text-sm text-n-ruby-11"
        >
          {{ previewErrorByFlowId[flow.id] }}
        </div>

        <div
          v-if="previewByFlowId[flow.id]"
          class="mt-3 rounded-xl border border-n-container bg-n-background p-3"
        >
          <div class="flex flex-wrap items-center justify-between gap-3">
            <p class="mb-0 text-xs font-medium uppercase tracking-wide text-n-slate-10">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_PREVIEW') }}
            </p>
            <div class="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="faded"
                color="slate"
                icon="i-ph-copy"
                :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_COPY_PREVIEW')"
                @click="copyPreviewUrl(flow.id)"
              />
              <a
                :href="previewByFlowId[flow.id]"
                target="_blank"
                rel="noopener noreferrer"
                class="text-sm font-medium text-n-brand hover:underline"
              >
                {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_PREVIEW_OPEN_LINK') }}
              </a>
            </div>
          </div>
          <p class="mt-2 mb-3 break-all text-xs text-n-slate-11">
            {{ previewByFlowId[flow.id] }}
          </p>
          <iframe
            :src="previewByFlowId[flow.id]"
            class="h-[28rem] w-full rounded-lg border border-n-container bg-white"
            :title="`${flow.name || flow.id}-preview`"
          />
        </div>

        <div
          v-else-if="previewPayloadByFlowId[flow.id]"
          class="mt-3 rounded-xl border border-n-container bg-n-background p-3"
        >
          <p class="mb-2 text-xs font-medium uppercase tracking-wide text-n-slate-10">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_PREVIEW_PAYLOAD') }}
          </p>
          <pre class="mb-0 overflow-auto text-xs text-n-slate-11">{{ JSON.stringify(previewPayloadByFlowId[flow.id], null, 2) }}</pre>
        </div>

        <div
          v-if="editingFlowId === flow.id"
          class="mt-3 grid gap-3 rounded-xl border border-n-container bg-n-background p-3"
        >
          <div class="grid gap-3 lg:grid-cols-2">
            <woot-input
              v-model="editForm.name"
              :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_NAME')"
            />
            <woot-input
              v-model="editForm.endpoint_uri"
              :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_ENDPOINT')"
            />
          </div>

          <div class="flex flex-wrap gap-2">
            <button
              v-for="option in categoryOptions"
              :key="`${flow.id}-${option.value}`"
              type="button"
              class="rounded-full border px-3 py-1.5 text-xs font-medium transition-colors"
              :class="
                editForm.categories.includes(option.value)
                  ? 'border-n-brand bg-n-brand/10 text-n-brand'
                  : 'border-n-container bg-n-background text-n-slate-11 hover:border-n-brand'
              "
              @click="toggleCategory(option.value, 'edit')"
            >
              {{ option.label }}
            </button>
          </div>

          <div class="flex justify-end gap-2">
            <Button
              type="button"
              variant="faded"
              color="slate"
              :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_CANCEL_CREATE')"
              @click="cancelEditing"
            />
            <Button
              type="button"
              color="blue"
              icon="i-ph-floppy-disk"
              :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_SAVE')"
              @click="updateFlow(flow)"
            />
          </div>
        </div>

        <div
          v-if="assetsByFlowId[flow.id]?.length"
          class="mt-3 rounded-xl border border-n-container bg-n-background p-3"
        >
          <p class="mb-2 text-xs font-medium uppercase tracking-wide text-n-slate-10">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_ASSETS') }}
          </p>
          <pre class="mb-0 overflow-auto text-xs text-n-slate-11">{{ JSON.stringify(assetsByFlowId[flow.id], null, 2) }}</pre>
        </div>

        <div
          v-if="metricsByFlowId[flow.id]"
          class="mt-3 rounded-xl border border-n-container bg-n-background p-3"
        >
          <p class="mb-2 text-xs font-medium uppercase tracking-wide text-n-slate-10">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_METRICS') }}
          </p>
          <div
            v-if="collectMetricSummary(metricsByFlowId[flow.id]).length"
            class="mb-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-3"
          >
            <div
              v-for="metric in collectMetricSummary(metricsByFlowId[flow.id])"
              :key="`${flow.id}-${metric.key}`"
              class="rounded-xl border border-n-container bg-n-alpha-2 p-3"
            >
              <p class="mb-1 text-xs text-n-slate-10">
                {{ metric.label }}
              </p>
              <p class="mb-0 text-lg font-semibold text-n-slate-12">
                {{ metric.value }}
              </p>
            </div>
          </div>
          <pre class="mb-0 overflow-auto text-xs text-n-slate-11">{{ JSON.stringify(metricsByFlowId[flow.id], null, 2) }}</pre>
        </div>
      </div>
    </div>
  </div>
</template>
